import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
} from "@mui/material";
import axios from "axios";
import {
  useGetAllInvoiceQuery,
  useCreateEmailInvoiceMutation,
  useGetWorkOrderQuery,
} from "../../../Api/attoDeskApi";
import { useStyles } from "../../../theme/appStyles";
import { useNavigate } from "react-router";
import {
  ICompanyDetails,
  ICustomer,
  ICustomerProject,
} from "../../../Api/Interface/api.interface";
import dayjs, { Dayjs } from "dayjs";
import {
  ProductType,
  TransferType,
  WorkOrderStatus,
} from "../../../Core/Enum/enum";
import { useParams } from "react-router-dom";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ReactToPrint from "react-to-print";
import LocalPrintshopSharpIcon from "@mui/icons-material/LocalPrintshopSharp";
import EmailIcon from "@mui/icons-material/Email";
import { useNotifier } from "../../../Core/Notifier";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// ─── Helpers ────────────────────────────────────────────────────────────────

const getStatusLabel = (statusCode: number): string => {
  switch (statusCode) {
    case WorkOrderStatus.Paid:    return "Paid";
    case WorkOrderStatus.Cancel:  return "Canceled";
    case WorkOrderStatus.Pending: return "Pending";
    default:                      return "Unknown Status";
  }
};

const getTransferType = (statusCode: string): string => {
  switch (statusCode) {
    case TransferType.OneWayTransfer:   return "One Way Transfer";
    case TransferType.RoundTrip:        return "Round Trip";
    case TransferType.MultiDestination: return "Multi Destination";
    default:                            return "Unknown Status";
  }
};

// ─── Interfaces ──────────────────────────────────────────────────────────────

interface IProduct {
  productName: string;
  productDescription: string;
  productImg: String | null;
  numberOfPassengers: number | null;
  transferType: string;
  transferTypeFrom: string;
  transferTypeTo: string;
  transferTypeDescription: string;
  departureDateTime: Dayjs | null;
  returnDateTime: Dayjs | null;
  departureAddress: string;
  destinationAddress: string;
  multiDestination: string;
  quantity: number | null;
  busCount?: number | null;
  price: number | null;
  tax: number | null;
  taxAmount: number | null;
  total: number;
  taxId: number | null;
  unitPriceWithDiscount: number | null;
  unitTotalWithDiscount: number;
  productType: string;
  productLongDescription: string;
  productShortDescription: string;
}

interface IDiscount {
  value: number | null;
  description: string;
}

interface IQuoteStop {
  type: string;
  num: number;
  description: string;
  address: string;
  city: string;
  dateTime: string;
}

interface IQuoteCustomer {
  name: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  province: string;
  postalCode: string;
}

interface IQuoteData {
  quoteNo: string;
  tripId: string;
  tripName: string;
  issueDate: string;
  dueDate: string;
  tripDate: string;
  customer: IQuoteCustomer;
  stops: IQuoteStop[];
  subTotal: string | number;
  discount: string | number;
  grandTotal: string | number;
  memo: string | null;
  remark: string | null;
}

interface IQuoteWorkOrder {
  id: number;
  quoteWorkOrderCompanyId: number;
  quoteWorkOrderCustomerId: string;
  quoteWorkOrderCustomerProjectId: string;
  quoteWorkOrderNo: string;
  quoteWorkOrderIssueDate: Dayjs;
  quoteWorkOrderDueDate: Dayjs;
  quoteTripId: string;
  quoteTripName: string;
  quoteWorkOrderAmount: number | null;
  quoteWorkOrderStatus: number;
  quoteWorkOrderSale: IProduct[];
  quoteWorkOrderDiscount: string;
  quoteWorkOrderSubTotal: string;
  quoteWorkOrderSaleGrandTotal: string;
  quoteWorkOrderSaleDiscountDescription: IDiscount[];
  customer: ICustomer;
  company: ICompanyDetails;
  project: ICustomerProject;
  quoteWorkOrderCustomerRemark: string;
  quoteWorkOrderCustomerMemo: string;
  quoteWorkOrderSaleDiscount: string;
  quoteWorkOrderSaleSubTotal: string;
  created_by: number;
  created_at: string;
  mainStatus: number;
  invoiceId: number;
  InvoiceNo: string;
  amount_paid: number;
  isInvoice: boolean;
  quoteData?: IQuoteData;
}

interface ViewWorkOrderProps {
  quoteId: string;
  type: string;
}

export interface ViewWorkOrderHandle {
  generatePdfBlob: () => Promise<Blob | null>;
  getContentElement: () => HTMLDivElement | null;
}

// ─── Ordinal suffix helper ────────────────────────────────────────────────────
const ordinal = (n: number) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// ─── Component ───────────────────────────────────────────────────────────────

const ViewWorkOrder = forwardRef<ViewWorkOrderHandle, ViewWorkOrderProps>(
  ({ quoteId, type }, ref) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { id: QuoteId } = useParams<{ id: string }>();
    const componentRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      getContentElement: () => componentRef.current,
      generatePdfBlob: async (): Promise<Blob | null> => {
        if (!componentRef.current) return null;
        const { default: jsPDFLib } = await import("jspdf");
        const { default: html2canvasLib } = await import("html2canvas");

        const imgs = Array.from(componentRef.current.querySelectorAll("img"));
        await Promise.all(
          imgs.map(async (img) => {
            const src = img.getAttribute("src") || "";
            if (!src || src.startsWith("data:")) return;
            try {
              const res = await axios.get(src, {
                responseType: "blob",
                headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
              });
              const dataUrl = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target?.result as string);
                reader.readAsDataURL(res.data);
              });
              img.src = dataUrl;
              await new Promise<void>((resolve) => {
                if (img.complete && img.naturalWidth > 0) { resolve(); return; }
                img.onload = () => resolve();
                img.onerror = () => resolve();
              });
            } catch { /* leave original src */ }
          })
        );

        const canvas = await html2canvasLib(componentRef.current, {
          scale: 1, useCORS: true, logging: false,
        });
        const imgData = canvas.toDataURL("image/jpeg", 0.7);
        const pdfW = 210;
        const pdfH = (canvas.height * pdfW) / canvas.width;
        const pdf = new jsPDFLib({ unit: "mm", format: [pdfW, pdfH] });
        pdf.addImage(imgData, "JPEG", 0, 0, pdfW, pdfH);
        return pdf.output("blob");
      },
    }));

    const [newEmailInvoice, { isLoading: isEmailSending }] = useCreateEmailInvoiceMutation();
    const { showErrorMessage, showMessage } = useNotifier();
    const [dataLoaded, setDataLoaded] = useState(false);
    const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;
    const API_URL = process.env.REACT_APP_API_URL;
    const [totalPrice, setTotalPrice] = useState(0);
    const [totalTax, setTotalTax] = useState(0);
    const [payments, setPayments] = useState<{ amount: number }[]>([]);
    const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);

    const { data: workOrderData, isLoading: workIsLoading, isError: workOrderError, refetch: workOrderRefetch } =
      useGetWorkOrderQuery({ searchText: "", id: String(quoteId) }, { refetchOnMountOrArgChange: true });

    const { data: invoicesData } = useGetAllInvoiceQuery(
      { searchText: "", id: "", quoteWorkOrderCustomerId: "", quoteWorkOrderCustomerProjectId: "" },
      { refetchOnMountOrArgChange: true }
    );

    const WorkOrderDataList = useMemo(() => {
      if (!workOrderData || !Array.isArray(workOrderData.data) || workOrderData.data.length === 0) return null;
      const wo = workOrderData.data[0] as any;
      const invList = Array.isArray(invoicesData?.data) ? (invoicesData!.data as any[]) : [];
      const inv = invList.find(
        (i: any) =>
          String(i.work_order_id) === String(wo.id) ||
          (wo.invoiceId && String(i.id) === String(wo.invoiceId))
      );
      if (inv) {
        return {
          ...wo,
          InvoiceNo: inv.InvoiceNo ?? wo.InvoiceNo,
          po_number: inv.po_number ?? wo.po_number,
          po_document: inv.po_document ?? wo.po_document,
          quoteWorkOrderSale: inv.quoteWorkOrderSale,
          quoteWorkOrderDiscount: inv.quoteWorkOrderDiscount,
          quoteWorkOrderSubTotal: inv.quoteWorkOrderSubTotal,
          quoteWorkOrderSaleGrandTotal: inv.quoteWorkOrderSaleGrandTotal,
          quoteWorkOrderSaleDiscountDescription: inv.quoteWorkOrderSaleDiscountDescription,
          quoteWorkOrderCustomerRemark: inv.quoteWorkOrderCustomerRemark,
          quoteWorkOrderCustomerMemo: inv.quoteWorkOrderCustomerMemo,
          amount_paid: inv.amount_paid,
          due_amount: inv.due_amount,
          paymentStatus: inv.paymentStatus,
          isInvoice: true,
          invoiceId: inv.id,
        } as IQuoteWorkOrder;
      }
      return wo as IQuoteWorkOrder;
    }, [workOrderData, invoicesData]);

    // Fetch company logo as base64
    useEffect(() => {
      const logoPath = WorkOrderDataList?.company?.CompanyLogo;
      if (!logoPath || !LARAVEL_API_URL) return;
      axios
        .get(`${LARAVEL_API_URL}/${logoPath}`, {
          responseType: "blob",
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        })
        .then((res) => {
          const reader = new FileReader();
          reader.onload = (e) => setLogoDataUrl(e.target?.result as string);
          reader.readAsDataURL(res.data);
        })
        .catch(() => setLogoDataUrl(null));
    }, [WorkOrderDataList?.company?.CompanyLogo, LARAVEL_API_URL]);

    const formatAddressReadOnly = (sn: string, st: string, city: string, prov: string, pc: string) =>
      `${sn} ${st}, ${city}, ${prov}, ${pc}`;

    const initialCompanyAddress = formatAddressReadOnly(
      WorkOrderDataList?.company.companyStreetNumber || "",
      WorkOrderDataList?.company.companyStreetName || "",
      WorkOrderDataList?.company.companyCity || "",
      WorkOrderDataList?.company.companyProvince || "",
      WorkOrderDataList?.company.companyPostalCode || ""
    );

    const coursings = Array.isArray(WorkOrderDataList?.quoteWorkOrderSale)
      ? WorkOrderDataList!.quoteWorkOrderSale
      : [];

    const discountList = Array.isArray(WorkOrderDataList?.quoteWorkOrderSaleDiscountDescription)
      ? WorkOrderDataList!.quoteWorkOrderSaleDiscountDescription
      : [];

      useEffect(() => {
  // console.log("quoteId prop:", quoteId);
  // console.log("workOrderData:", workOrderData);
  // console.log("WorkOrderDataList:", WorkOrderDataList);
}, [quoteId, workOrderData, WorkOrderDataList]);

    useEffect(() => {
      setDataLoaded(!!(WorkOrderDataList && WorkOrderDataList.project));
    }, [workOrderData]);

    const handleSendEmail = async () => {
      try {
        if (!componentRef.current) { showErrorMessage("The invoice component is not available."); return; }
        if (!dataLoaded) { showErrorMessage("Data is still loading. Please wait."); return; }
        if (!WorkOrderDataList || !WorkOrderDataList.project) {
          showErrorMessage("Required work order or project data is missing."); return;
        }
        const pdf = new jsPDF();
        const canvas = await html2canvas(componentRef.current);
        const imgData = canvas.toDataURL("image/png");
        pdf.addImage(imgData, "PNG", 0, 0, pdf.internal.pageSize.getWidth(),
          (canvas.height * pdf.internal.pageSize.getWidth()) / canvas.width);
        const file = new File([pdf.output("blob")], `${WorkOrderDataList?.InvoiceNo}.pdf`, { type: "application/pdf" });
        const formData = new FormData();
        formData.append("customer_id", WorkOrderDataList?.project.customerId.toString() || "");
        formData.append("project_id", WorkOrderDataList?.project.id.toString() || "");
        formData.append("to", WorkOrderDataList?.project.projectEmail || "");
        formData.append("subject", "Invoice for Your Project");
        formData.append("message", "Please find attached the invoice for your project.");
        formData.append("invoice", file);
        const emailResponse = await newEmailInvoice(formData).unwrap();
        if (!emailResponse) showErrorMessage("Failed to send email");
        else showMessage("Email sent successfully");
      } catch (error) { console.error("Error sending email:", error); }
    };

    useEffect(() => {
      let newTotalPrice = 0, newTotalTax = 0;
      coursings.forEach((p) => {
        newTotalPrice += p.total > 0 ? p.total : (p.price ?? 0) * (p.busCount ?? p.quantity ?? 1);
        newTotalTax += p.taxAmount ?? 0;
      });
      setTotalPrice(newTotalPrice);
      setTotalTax(newTotalTax);
    }, [coursings]);

    useEffect(() => {
      if (!WorkOrderDataList?.id || !API_URL) return;
      axios
        .get(`${API_URL}/payments-by-workorder?work_order_id=${WorkOrderDataList.id}`)
        .then((res) => setPayments((res.data || []).filter((p: any) => p.is_deleted === 0 && p.is_return === 0)))
        .catch(() => setPayments([]));
    }, [WorkOrderDataList?.id, API_URL]);

    // ─── Computed values ─────────────────────────────────────────────────────
    const grandTotal    = parseFloat(WorkOrderDataList?.quoteWorkOrderSaleGrandTotal || "0");
    const totalDiscount = parseFloat(WorkOrderDataList?.quoteWorkOrderDiscount || "0");
    const subtotal      = Math.max(0, grandTotal - totalTax);

    const fmtTime = (val: any): string => {
      if (!val) return "";
      const d = dayjs(val);
      return d.isValid() ? d.format("h:mm a") : "";
    };
    const toTitleCase = (str: string): string =>
      str ? str.replace(/\b\w/g, (c) => c.toUpperCase()) : "";
    const fmtDateTime = (val: any): string => {
      if (!val) return "";
      const d = dayjs(val);
      return d.isValid() ? d.format("MMM D, YYYY h:mm A") : "";
    };

    const bdr = "1px solid #000";
    const labelCell: React.CSSProperties = { border: bdr, padding: "5px 8px", fontWeight: "bold", whiteSpace: "nowrap", width: "18%" };
    const valueCell: React.CSSProperties = { border: bdr, padding: "5px 8px", width: "32%" };
    const addrBody:  React.CSSProperties = { border: bdr, padding: "6px 8px", fontWeight: "bold" };
    const detCell:   React.CSSProperties = { border: bdr, padding: "6px 8px" };

    const clientName = WorkOrderDataList?.quoteWorkOrderCustomerProjectId
      ? WorkOrderDataList?.project?.projectName || ""
      : (() => {
          const parts = [WorkOrderDataList?.customer?.customerFirstName, WorkOrderDataList?.customer?.customerLastName]
            .filter(Boolean)
            .map((s) => (s || "").charAt(0).toUpperCase() + (s || "").slice(1).toLowerCase())
            .join(" ");
          return parts || WorkOrderDataList?.customer?.customerCompanyName || "";
        })();

    const template           = (WorkOrderDataList?.company as any)?.company_document_template;
    // const isBusLinesTemplate = template === "invoice";
    // const isActualInvoice    = WorkOrderDataList?.isInvoice || !!WorkOrderDataList?.invoiceId;
    const showInvoice = template === "invoice";

    // ─── Inline styles reused throughout the invoice ──────────────────────────
    const invoiceWrap: React.CSSProperties = {
      backgroundColor: "#ffffff",
      color: "#000000",
      fontFamily: "Arial, Helvetica, sans-serif",
      fontSize: "13px",
      maxWidth: "860px",
      margin: "0 auto",
      padding: "28px 40px 32px 40px",
      position: "relative",
    };

    return (
      <Box>
        {/* ── Print / Email buttons ── */}
        {type !== "email" && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 1 }}>
            <ReactToPrint
              trigger={() => (
                <Button
                  variant="contained"
                  startIcon={<LocalPrintshopSharpIcon />}
                  sx={{
                    background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
                    color: "#fff", borderRadius: 2, px: 3, fontWeight: 600, textTransform: "none",
                    boxShadow: "0 4px 12px rgba(30,41,59,0.3)",
                    "&:hover": { background: "linear-gradient(135deg, #334155 0%, #1e293b 100%)" },
                  }}
                >
                  Print
                </Button>
              )}
              content={() => componentRef.current}
              documentTitle={WorkOrderDataList?.quoteWorkOrderNo || "Quote"}
              pageStyle="@page { size: A4 portrait; margin: 8mm 10mm; } @media print { * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; } }"
            />
          </Box>
        )}

        <div ref={componentRef}>
          {showInvoice ? (
            /* ══════════════════════════════════════════════════════════════
               INVOICE FORMAT — matches INVOICE_tjbus_lines_13797 PDF
               ══════════════════════════════════════════════════════════════ */
            <div style={invoiceWrap}>

              {/* Watermark */}
              {!!WorkOrderDataList?.company.CompanyWatermark && (
                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "60%", opacity: 0.06, pointerEvents: "none", zIndex: 0 }}>
                  <img src={`${LARAVEL_API_URL}/${WorkOrderDataList.company.CompanyWatermark}`} alt="Watermark" style={{ width: "100%" }} />
                </div>
              )}

              {/* ── HEADER: Logo left | italic INVOICE + address right ── */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "relative", zIndex: 1 }}>

                {/* Left: Logo */}
                <div style={{ width: "56%" }}>
                  {WorkOrderDataList?.company?.CompanyLogo ? (
                    <img
                      src={logoDataUrl || `${LARAVEL_API_URL}/${WorkOrderDataList.company.CompanyLogo}`}
                      alt="Company Logo"
                      style={{ maxWidth: "100%", height: "auto", minHeight: "80px", maxHeight: "130px", objectFit: "contain", objectPosition: "left center", display: "block" }}
                    />
                  ) : (
                    <div style={{ width: "80px", height: "80px", borderRadius: "50%", background: "#2f3891", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "36px", fontWeight: "bold", color: "#fff" }}>
                      {(WorkOrderDataList?.company?.companyName || "C").charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                {/* Right: italic INVOICE title + address block */}
                <div style={{ width: "40%", textAlign: "left" }}>
                  {/* "INVOICE" in italic serif — matches PDF exactly */}
                  <div style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontStyle: "italic", fontWeight: "bold", fontSize: "30px", marginBottom: "8px" ,  textAlign: "right",}}>
                    <em>INVOICE</em>
                  </div>

                  {/* Company address block */}
                  <div style={{ fontSize: "13px", fontWeight: "bold", lineHeight: "1.65" }}>
                    <div>
                      {[WorkOrderDataList?.company.companyStreetNumber, WorkOrderDataList?.company.companyStreetName]
                        .filter(Boolean).join(" ")}
                    </div>
                    <div>
                      {WorkOrderDataList?.company.companyCity
                        ? `${WorkOrderDataList.company.companyCity}, ${WorkOrderDataList.company.companyProvince}.`
                        : ""}
                    </div>
                    <div>{WorkOrderDataList?.company.companyPostalCode || ""}</div>
                    {WorkOrderDataList?.company?.companyMobileNumber && (
                      <div>Phone: {WorkOrderDataList.company.companyMobileNumber}</div>
                    )}
                    <div style={{ marginTop: "2px" }}>
                      {WorkOrderDataList?.company?.companyEmail && (
                        <a
                          href={`mailto:${WorkOrderDataList.company.companyEmail}`}
                          style={{ color: "#1a0dab", textDecoration: "underline", fontWeight: "bold" }}
                        >
                          {WorkOrderDataList.company.companyEmail}
                        </a>
                      )}
                      {WorkOrderDataList?.company?.companyWebSiteLink && (
                        <span style={{ marginLeft: "14px" }}>{WorkOrderDataList.company.companyWebSiteLink}</span>
                      )}
                    </div>
                  </div>

                  {/* Invoice # and Date */}
                  <div style={{ marginTop: "10px", fontSize: "13px", fontWeight: "bold", lineHeight: "1.55" }}>
                    <div>Invoice #{WorkOrderDataList?.InvoiceNo || WorkOrderDataList?.quoteWorkOrderNo || ""}</div>
                    <div>
                      Invoice{" "}
                      {WorkOrderDataList?.quoteWorkOrderIssueDate
                        ? dayjs(WorkOrderDataList.quoteWorkOrderIssueDate).format("MMMM D/YYYY")
                        : WorkOrderDataList?.created_at
                        ? dayjs(WorkOrderDataList.created_at).format("MMMM D/YYYY")
                        : ""}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Divider ── */}
              <hr style={{ border: "none", borderTop: "1.5px solid #000", margin: "18px 0 16px 0", position: "relative", zIndex: 1 }} />

              {/* ── Company Name ── */}
              <div style={{ marginBottom: "16px", fontSize: "17px", fontWeight: "bold", position: "relative", zIndex: 1 }}>
                <strong>Company Name: </strong>{clientName}
              </div>

              {/* ── PICK UP / DROP OFF ── */}
              {coursings.map((product, pi) => {
                const pickups:  any[] = (product as any).pickups  || [];
                const dropoffs: any[] = (product as any).dropoffs || [];
                return (
                  <div key={pi} style={{ marginBottom: "12px", position: "relative", zIndex: 1 }}>
                    {/* Pickups */}
                    {pickups.length > 0 ? (
                      <div style={{ marginBottom: "8px", lineHeight: "1.7" }}>
                        <strong style={{ fontSize: "14px" }}>PICK UP:&nbsp;&nbsp;</strong>
                        {pickups.map((pu: any, idx: number) => (
                          <React.Fragment key={idx}>
                            {idx > 0 && <br />}
                            {/* indent subsequent pickups to align with first */}
                            {idx > 0 && <span style={{ marginLeft: "85px" }} />}
                            {pickups.length > 1 ? `${ordinal(idx + 1)} Pickup – ` : ""}
                            {[pu.address, pu.city].filter(Boolean).join(", ")}
                          </React.Fragment>
                        ))}
                      </div>
                    ) : product.departureAddress ? (
                      <div style={{ marginBottom: "8px" }}>
                        <strong style={{ fontSize: "14px" }}>PICK UP:&nbsp;&nbsp;</strong>
                        {product.departureAddress}
                      </div>
                    ) : null}

                    {/* Dropoffs */}
                    {dropoffs.length > 0 ? (
                      <div style={{ marginBottom: "8px", lineHeight: "1.7" }}>
                        <strong style={{ fontSize: "14px" }}>DROP OFF:&nbsp;&nbsp;</strong>
                        {dropoffs.map((dr: any, idx: number) => (
                          <React.Fragment key={idx}>
                            {idx > 0 && <br />}
                            {[dr.address, dr.city].filter(Boolean).join(", ")}
                          </React.Fragment>
                        ))}
                      </div>
                    ) : product.destinationAddress ? (
                      <div style={{ marginBottom: "8px" }}>
                        <strong style={{ fontSize: "14px" }}>DROP OFF:&nbsp;&nbsp;</strong>
                        {product.destinationAddress}
                      </div>
                    ) : null}
                  </div>
                );
              })}

              {/* ── Start time / Return Time on one line ── */}
              {coursings.length > 0 && (() => {
                const product = coursings[0];
                const puList  = ((product as any).pickups  || []).filter((p: any) => p.address || p.city);
                const drList  = ((product as any).dropoffs || []).filter((d: any) => d.address || d.city);
                const rdrList = ((product as any).returnDropoffs || []).filter((d: any) => d.address || d.city);
                const startTime = fmtTime(puList[0]?.dateTime || puList[0]?.time || product.departureDateTime).toUpperCase();
                const endTime   = fmtTime(
                  rdrList[rdrList.length - 1]?.dateTime || rdrList[rdrList.length - 1]?.time ||
                  drList[drList.length - 1]?.dateTime   || drList[drList.length - 1]?.time   ||
                  product.returnDateTime
                ).toUpperCase();
                if (!startTime && !endTime) return null;
                return (
                  <div style={{ display: "flex", gap: "80px", marginBottom: "16px", fontSize: "14px", fontWeight: "bold", position: "relative", zIndex: 1 }}>
                    {startTime && <div><strong>Start time:&nbsp;</strong>{startTime}</div>}
                    {endTime   && <div><strong>Return Time&nbsp;</strong>{endTime}</div>}
                  </div>
                );
              })()}

              {/* ── Description / Charges Table ── */}
            {/* ── Description / Charges Table ── */}
<table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", position: "relative", zIndex: 1 }}>
  <thead>
    <tr>
      <th style={{ border: "1px solid #000", padding: "7px 14px", textAlign: "center", fontWeight: "bold" }}>Description</th>
      <th style={{ border: "1px solid #000", padding: "7px 14px", textAlign: "right", fontWeight: "bold", width: "22%" }}>Charges</th>
    </tr>
  </thead>
  <tbody>
    {coursings.map((product, pi) => {
      const qty       = product.busCount ?? product.quantity ?? 1;
      const price     = product.price ?? 0;
      const lineTotal = product.total > 0 ? product.total : price * qty;
      const desc      = product.productLongDescription || product.productShortDescription || product.productDescription || "";
      return (
        <React.Fragment key={pi}>
          <tr>
            <td style={{ border: "1px solid #000", padding: "7px 14px", textAlign: "center" }}>
              {product.productName || ""}
            </td>
            {/* ── same 30/70 inner table as summary rows so $ aligns perfectly ── */}
            <td style={{ border: "1px solid #000", padding: "0" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "70%" }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td style={{ padding: "7px 4px 7px 10px", whiteSpace: "nowrap", verticalAlign: "middle" }}>
                      $
                    </td>
                    <td style={{ padding: "7px 10px 7px 4px", textAlign: "right", whiteSpace: "nowrap", verticalAlign: "middle" }}>
                      {lineTotal.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
          {desc.trim() !== "" && (
            <tr>
              <td style={{ border: "1px solid #000", padding: "7px 14px", textAlign: "center", color: "#333", fontSize: "12px" }}>
                {desc}
              </td>
              <td style={{ border: "1px solid #000", padding: "7px 14px" }}></td>
            </tr>
          )}
        </React.Fragment>
      );
    })}
    {/* Empty padding rows */}
    {Array.from({ length: Math.max(0, 6 - coursings.length * 2) }).map((_, i) => (
      <tr key={`empty-${i}`}>
        <td style={{ border: "1px solid #000", padding: "7px 14px", height: "30px" }}></td>
        <td style={{ border: "1px solid #000", padding: "7px 14px" }}></td>
      </tr>
    ))}
  </tbody>
</table>

            

                 
                    {/* PAID / BALANCE DUE rows (only if payment recorded) */}
                  {/* ── AMOUNT / HST TAX / TOTAL — full width, columns match Description + Charges above ── */}
{(() => {
  const totalPaid = payments.reduce((s, p) => s + Number(p.amount || 0), 0)
    || Number(WorkOrderDataList?.amount_paid || 0);
  const balance = Math.max(0, grandTotal - totalPaid);

  const summaryRows: { label: string; amount: number; bold?: boolean; color?: string; isNeg?: boolean }[] = [
    { label: "AMOUNT",  amount: subtotal },
    ...(totalTax > 0 ? [{ label: "HST TAX", amount: totalTax }] : []),
    ...(discountList.length > 0 && totalDiscount > 0
          ? [{ label: "DISCOUNT", amount: totalDiscount, color: "#dc2626", isNeg: true }] : []),
    { label: "TOTAL", amount: grandTotal, bold: true },
    ...(totalPaid > 0 ? [
      { label: "PAID",        amount: totalPaid, color: "#16a34a", isNeg: true },
      { label: "BALANCE DUE", amount: balance,   color: balance > 0 ? "#dc2626" : "#16a34a", bold: true },
    ] : []),
  ];

  return (
    <table style={{
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "13px",
      marginTop: "-1px",
      position: "relative",
      zIndex: 1,
      tableLayout: "fixed",
    }}>
      <colgroup>
        <col style={{ width: "78%" }} />
        <col style={{ width: "22%" }} />
      </colgroup>
      <tbody>
        {summaryRows.map(({ label, amount, bold, color, isNeg }) => (
          <tr key={label}>
            {/* Label cell — NO border at all on left side, only right-align text */}
            <td style={{
              padding: "4px 8px 4px 0",
              textAlign: "right",
              fontWeight: "bold",
              color: color || "#000",
              whiteSpace: "nowrap",
              border: "none",           // no border on label side
            }}>
              {label}
            </td>

            {/* Charges cell — split into $ column | number column using inner table */}
            <td style={{
              border: "1px solid #000",
              padding: "0",
            }}>
              <table style={{
                width: "100%",
                borderCollapse: "collapse",
                tableLayout: "fixed",
              }}>
                <colgroup>
                  <col style={{ width: "30%" }} />   {/* $ sign column */}
                  <col style={{ width: "70%" }} />   {/* number column */}
                </colgroup>
                <tbody>
                  <tr>
                    {/* $ sign — left side of Charges cell */}
                    <td style={{
                      padding: "4px 4px 4px 10px",
                      fontWeight: bold ? "bold" : "normal",
                      color: color || "#000",
                      whiteSpace: "nowrap",
                      verticalAlign: "middle",
                    }}>
                      {isNeg ? "-$" : "$"}
                    </td>
                    {/* Number — right side, right-aligned */}
                    <td style={{
                      padding: "4px 10px 4px 4px",
                      textAlign: "right",
                      fontWeight: bold ? "bold" : "normal",
                      color: color || "#000",
                      whiteSpace: "nowrap",
                      verticalAlign: "middle",
                    }}>
                      {Math.abs(amount).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
})()}
                 

              {/* ── HST Number ── */}
             {WorkOrderDataList?.company?.companyHstNo && (
  <div style={{ marginTop: "10px", fontSize: "13px", textAlign: "center", position: "relative", zIndex: 1 }}>
    HST;{WorkOrderDataList.company.companyHstNo}
  </div>
)}
              {/* ── Comments ── */}
              <div style={{ marginTop: "18px", fontSize: "13px", fontWeight: "bold", position: "relative", zIndex: 1 }}>
                Comments:
              </div>
              {WorkOrderDataList?.quoteWorkOrderCustomerRemark?.trim() && (
                <div style={{ fontSize: "13px", marginTop: "4px", position: "relative", zIndex: 1 }}>
                  {WorkOrderDataList.quoteWorkOrderCustomerRemark}
                </div>
              )}

              {/* ── Signature Lines — dotted ── */}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "44px", fontSize: "12px", position: "relative", zIndex: 1 }}>
                <div>
                  <div style={{ borderBottom: "1.5px dotted #555", width: "260px", marginBottom: "5px", height: "18px" }} />
                  <strong>Customers Signature</strong>
                </div>
                <div>
                  <div style={{ borderBottom: "1.5px dotted #555", width: "260px", marginBottom: "5px", height: "18px" }} />
                  <strong>Driver/Operator Name</strong>
                </div>
              </div>

              {/* ── Footer — cancellation policy ── */}
              <div style={{ marginTop: "22px", fontSize: "12px", fontWeight: "bold", position: "relative", zIndex: 1, lineHeight: "1.5" }}>
                {WorkOrderDataList?.company?.companyFooterDetails?.trim() ? (
                  WorkOrderDataList.company.companyFooterDetails
                    .split("\n")
                    .map((line, i) => <div key={i}>{line}</div>)
                ) : (
                  <div>
                    CANCELLATION POLICY: IF THE CUSTOMER FAILS TO CANCEL THIS CONFORMATION 8 DAYS PRIOR TO THE
                    TRIP, THE CUSTOMER WILL BE RESPONSIBLE TO COMPENSATE TJ BUS LINES LTD. 50% OF THE PRICE.
                  </div>
                )}
              </div>

            </div>
          ) : (
            /* ══════════════════════════════════════════════════════════════
               CHARTER CONFIRMATION FORMAT
               ══════════════════════════════════════════════════════════════ */
            <div style={{
              backgroundColor: "#ffffff", color: "#000000",
              fontFamily: "Arial, sans-serif", fontSize: "12px",
              width: "100%", boxSizing: "border-box", margin: "0", padding: "0",
              position: "relative",
            }}>

              {/* Watermark */}
              {!!WorkOrderDataList?.company.CompanyWatermark && (
                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "60%", opacity: 0.06, pointerEvents: "none", zIndex: 0 }}>
                  <img src={`${LARAVEL_API_URL}/${WorkOrderDataList.company.CompanyWatermark}`} alt="Watermark" style={{ width: "100%" }} />
                </div>
              )}

              {/* ── Header ── */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "20px 32px 4px 32px", position: "relative", zIndex: 1 }}>
                <div>
                  {WorkOrderDataList?.company?.CompanyLogo ? (
                    <img
                      src={logoDataUrl || `${LARAVEL_API_URL}/${WorkOrderDataList.company.CompanyLogo}`}
                      alt="Company Logo"
                      style={{ width: "320px", height: "auto", minHeight: "80px", maxHeight: "120px", objectFit: "contain", objectPosition: "left center", display: "block" }}
                    />
                  ) : (
                    <div style={{ width: "80px", height: "80px", borderRadius: "50%", background: "#2f3891", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "36px", fontWeight: "bold", color: "#fff" }}>
                      {(WorkOrderDataList?.company?.companyName || "C").charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: "right", paddingTop: "10px" }}>
                  <div style={{ fontSize: "20px", fontWeight: "bold", letterSpacing: "1px" }}>CHARTER CONFIRMATION</div>
                </div>
              </div>

              {/* ── Company Address ── */}
              <div style={{ padding: "4px 32px 0 32px", position: "relative", zIndex: 1, fontSize: "11px" }}>
                {initialCompanyAddress}
              </div>

              {/* ── Email + Tel + Website ── */}
              <div style={{ padding: "4px 32px 12px 32px", position: "relative", zIndex: 1 }}>
                <div style={{ fontSize: "15px", fontWeight: "bold", color: "#1a0dab" }}>
                  {WorkOrderDataList?.company?.companyEmail && (
                    <a href={`mailto:${WorkOrderDataList.company.companyEmail}`} style={{ color: "#1a0dab", textDecoration: "underline" }}>
                      {WorkOrderDataList.company.companyEmail}
                    </a>
                  )}
                  {WorkOrderDataList?.company?.companyMobileNumber && (
                    <span style={{ textDecoration: "underline" }}>Tel:{WorkOrderDataList.company.companyMobileNumber}</span>
                  )}
                  {WorkOrderDataList?.company?.companyWebSiteLink && (
                    <span style={{ textDecoration: "underline" }}> / {WorkOrderDataList.company.companyWebSiteLink}</span>
                  )}
                </div>
              </div>

              {/* ── Trip Details Table ── */}
              <div style={{ marginTop:'15px', padding: "0 32px", position: "relative", zIndex: 1 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                  <tbody>
                    <tr>
                      <td style={labelCell}>Trip ID:</td>
                      <td style={valueCell}>{(() => {
                        const prefix = (WorkOrderDataList?.company as any)?.companyTripPrefix || "";
                        const raw = WorkOrderDataList?.quoteTripId;
                        if (!raw) return "";
                        return prefix ? `${prefix}${String(raw).padStart(5, "0")}` : String(raw);
                      })()}</td>
                      <td style={labelCell}>Trip Name:</td>
                      <td style={valueCell}>{WorkOrderDataList?.quoteTripName || ""}</td>
                    </tr>
                    <tr>
                      <td style={labelCell}>Purchase Order #:</td>
                      <td style={valueCell}>{WorkOrderDataList?.quoteWorkOrderNo || ""}</td>
                      <td style={labelCell}>Trip Date:</td>
                      <td style={valueCell}>
                        {WorkOrderDataList?.quoteData?.tripDate || (() => {
                          const d = (coursings[0] as any)?.pickups?.[0]?.dateTime || (coursings[0] as any)?.pickups?.[0]?.time || coursings[0]?.departureDateTime;
                          return d ? dayjs(d).format("MMMM D/YYYY").toUpperCase() : "";
                        })()}
                      </td>
                    </tr>
                    <tr>
                      <td style={labelCell}>Customer</td>
                      <td style={valueCell}>{clientName}</td>
                      <td style={labelCell}>e-mail:</td>
                      <td style={valueCell}>{WorkOrderDataList?.customer?.customerEmail || WorkOrderDataList?.customer?.customerCompanyEmail || ""}</td>
                    </tr>
                    <tr>
                      <td style={labelCell}>Attention:</td>
                      <td style={valueCell}>
                        {WorkOrderDataList?.quoteWorkOrderCustomerProjectId
                          ? [WorkOrderDataList?.customer?.customerFirstName, WorkOrderDataList?.customer?.customerLastName].filter(Boolean).join(" ")
                          : ""}
                      </td>
                      <td style={labelCell}>State/Province:</td>
                      <td style={valueCell}>{WorkOrderDataList?.quoteData?.customer?.province || WorkOrderDataList?.customer?.customerProvince || ""}</td>
                    </tr>
                    <tr>
                      <td style={labelCell}>Address:</td>
                      <td style={valueCell}>
                        {WorkOrderDataList?.quoteData?.customer?.street || (
                          [WorkOrderDataList?.customer?.customerStreetNumber, WorkOrderDataList?.customer?.customerStreetName].filter(Boolean).join(" ") +
                          (WorkOrderDataList?.customer?.customerCity
                            ? `, ${WorkOrderDataList.customer.customerCity}, ${WorkOrderDataList.customer.customerProvince} ${WorkOrderDataList.customer.customerPostalCode}`
                            : "")
                        )}
                      </td>
                      <td style={labelCell}>Zip/Postal Code:</td>
                      <td style={valueCell}>{WorkOrderDataList?.quoteData?.customer?.postalCode || WorkOrderDataList?.customer?.customerPostalCode || ""}</td>
                    </tr>
                    <tr>
                      <td style={labelCell}></td>
                      <td style={valueCell}></td>
                      <td style={labelCell}>Phone:</td>
                      <td style={valueCell}>
                        {WorkOrderDataList?.quoteData?.customer?.phone ||
                          WorkOrderDataList?.customer?.customerMobileNum ||
                          (WorkOrderDataList?.customer as any)?.customerMobileNumber ||
                          (WorkOrderDataList?.customer as any)?.customerPhoneNumber || ""}
                      </td>
                    </tr>
                    <tr>
                      <td style={labelCell}>City:</td>
                      <td style={valueCell}>{WorkOrderDataList?.quoteData?.customer?.city || WorkOrderDataList?.customer?.customerCity || ""}</td>
                      <td style={labelCell}></td>
                      <td style={valueCell}></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* ── Route Table ── */}
              <div style={{ padding: "0 32px", marginTop:'15px', position: "relative", zIndex: 1 }}>
                {(() => {
                  const stops: IQuoteStop[] = WorkOrderDataList?.quoteData?.stops ?? [];
                  const rc: React.CSSProperties  = { border: bdr, padding: "6px 10px", fontWeight: "bold" };
                  const col1: React.CSSProperties = { ...rc, width: "30%" };
                  const col2: React.CSSProperties = { ...rc, width: "50%" };
                  const col3: React.CSSProperties = { ...rc, width: "20%", whiteSpace: "nowrap" as const };
                  return (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                      <thead>
                        <tr>
                          <th style={col1}>ADDRESS DESCRIPTION</th>
                          <th style={col2}>ADDRESS</th>
                          <th style={col3}>DEPARTURE TIME</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stops.length > 0 ? (
                          stops.map((stop, idx) => (
                            <tr key={idx}>
                              <td style={col1}>{toTitleCase(stop.description || stop.address.split(",")[0].trim())}</td>
                              <td style={col2}>{[stop.address, stop.city].filter(Boolean).join(", ")}</td>
                              <td style={col3}>{fmtTime(stop.dateTime)}</td>
                            </tr>
                          ))
                        ) : (
                          coursings.flatMap((product, pi) => {
                            const puList = ((product as any).pickups  || []).filter((p: any) => p.address || p.city);
                            const drList = ((product as any).dropoffs || []).filter((d: any) => d.address || d.city);
                            const rows: React.ReactElement[] = [];
                            puList.forEach((pu: any, idx: number) => rows.push(
                              <tr key={`pu-${pi}-${idx}`}>
                                <td style={col1}>{toTitleCase(pu.description || pu.address?.split(",")[0]?.trim() || "Pickup")}</td>
                                <td style={col2}>{[pu.address, pu.city].filter(Boolean).join(", ")}</td>
                                <td style={col3}>{fmtTime(pu.dateTime || pu.time || (idx === 0 ? product.departureDateTime : null))}</td>
                              </tr>
                            ));
                            if (puList.length === 0 && product.departureAddress) rows.push(
                              <tr key={`dep-${pi}`}>
                                <td style={col1}>{toTitleCase(product.departureAddress.split(",")[0].trim())}</td>
                                <td style={col2}>{product.departureAddress}</td>
                                {/* <td style={col3}>{fmtDateTime(product.departureDateTime)}</td> */}
                                <td style={col3}>{fmtTime(product.departureDateTime)}</td>
                              </tr>
                            );
                            drList.forEach((dr: any, idx: number) => rows.push(
                              <tr key={`dr-${pi}-${idx}`}>
                                <td style={col1}>{toTitleCase(dr.description || dr.address?.split(",")[0]?.trim() || "Dropoff")}</td>
                                <td style={col2}>{[dr.address, dr.city].filter(Boolean).join(", ")}</td>
                                <td style={col3}>{fmtTime(dr.dateTime || dr.time || (idx === 0 ? product.returnDateTime : null))}</td>
                              
                              </tr>
                            ));
                            if (drList.length === 0 && product.destinationAddress) rows.push(
                              <tr key={`dest-${pi}`}>
                                <td style={col1}>{toTitleCase(product.destinationAddress.split(",")[0].trim())}</td>
                                <td style={col2}>{product.destinationAddress}</td>
                                {/* <td style={col3}>{fmtDateTime(product.returnDateTime)}</td> */}
                                <td style={col3}>{fmtTime(product.returnDateTime)}</td>
                              </tr>
                            );
                            return rows;
                          })
                        )}
                        <tr>
                          <td colSpan={3} style={addrBody}>
                            {WorkOrderDataList?.quoteData?.remark?.trim()
                              ? `Special Notification: ${WorkOrderDataList.quoteData.remark}`
                              : "Special Notification."}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  );
                })()}
              </div>

              {/* ── Bus Details ── */}
              <div style={{ padding: "0 32px", position: "relative", zIndex: 1 }}>
                {coursings.map((product, pi) => {
                  const puList  = ((product as any).pickups        || []).filter((p: any) => p.address || p.city);
                  const drList  = ((product as any).dropoffs       || []).filter((d: any) => d.address || d.city);
                  const rdrList = ((product as any).returnDropoffs || []).filter((d: any) => d.address || d.city);
                  const startTime = fmtTime(puList[0]?.dateTime || puList[0]?.time || product.departureDateTime).toUpperCase();
                  const endTime   = fmtTime(
                    rdrList[rdrList.length - 1]?.dateTime || rdrList[rdrList.length - 1]?.time ||
                    drList[drList.length - 1]?.dateTime   || drList[drList.length - 1]?.time   ||
                    product.returnDateTime
                  ).toUpperCase();
                  const tripType = product.transferType ? getTransferType(product.transferType) : "";
                  return (
                    <table key={pi} style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                      <tbody>
                        <tr>
                          <td style={{ ...detCell, width: "20%" }}><strong>Bus Type:</strong><br />{product.productName || ""}</td>
                          <td style={{ ...detCell, width: "20%" }}><strong>Bus Quantity: </strong>{product.busCount || product.quantity || 1}</td>
                          {/* {tripType && <td style={{ ...detCell, width: "20%" }}><strong>Trip Type: </strong>{tripType}</td>} */}
                          <td style={{ ...detCell, width: tripType ? "20%" : "25%" }}><strong>Start Time; </strong>{startTime}</td>
                          <td style={{ ...detCell, width: tripType ? "20%" : "25%" }}><strong>End Time; </strong>{endTime}</td>
                        </tr>
                      </tbody>
                    </table>
                  );
                })}
              </div>

              {/* ── Financial Summary ── */}
              <div style={{ padding: "0 32px", position: "relative", zIndex: 1 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                  <tbody>
                    <tr>
                      <td style={{ ...detCell, width: "25%" }}><strong>Subtotal $ </strong>{subtotal.toFixed(2)}</td>
                      <td style={{ ...detCell, width: "25%" }}>
                        <strong>Additional</strong>
                        {discountList.length > 0 && totalDiscount > 0 ? `: -$${totalDiscount.toFixed(2)}` : "--------:"}
                      </td>
                      <td style={{ ...detCell, width: "25%" }}><strong>TAX: </strong>{totalTax.toFixed(2)}</td>
                      <td style={{ ...detCell, width: "25%" }}><strong>Total: </strong>{grandTotal.toFixed(2)}</td>
                    </tr>
                    {WorkOrderDataList?.company.companyHstNo && (
                      <tr>
                        <td colSpan={4} style={{ ...detCell, fontSize: "11px" }}>
                          HST: {WorkOrderDataList.company.companyHstNo}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* ── Footer Notes ── */}
            <div style={{ padding: "8px 32px 4px 32px", position: "relative", zIndex: 1, fontSize: "12px" }}>
  {(() => {
    const terms  = (WorkOrderDataList?.company as any)?.companyTermsAndConditions?.trim();
    const footer = (WorkOrderDataList?.company as any)?.companyFooterDetails?.trim();
    const text   = terms || footer;
    return text ? (
      text.split("\n").map((line: string, i: number) => <div key={i}>{line}</div>)
    ) : null;
  })()}
</div>

              {/* ── Signature Line ── */}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "16px 32px 8px 32px", position: "relative", zIndex: 1, fontSize: "12px", }}>
                <div>Signature ………………………………………………</div>
                <div>Date ………………………………</div>
              </div>

              {/* ── Company Name Footer ── */}
              {/* <div style={{ padding: "8px 32px 20px 32px", textAlign: "right", position: "relative", zIndex: 1, fontSize: "12px" }}>
                <span style={{ backgroundColor: "#cce5ff", padding: "4px 14px", fontWeight: "bold" }}>
                  {WorkOrderDataList?.company.companyName || ""}
                </span>
              </div> */}

            </div>
          )}
        </div>
      </Box>
    );
  }
);

ViewWorkOrder.displayName = "ViewWorkOrder";

export default ViewWorkOrder;